/**
 * FeedbackDashboard — Admin Command Center → "Feedback" tab.
 *
 * Surfaces every piece of feedback submitted via the Tutor Buddy widget,
 * with the AI's category/severity/sentiment/suggestion analysis. Admin
 * & support_staff can:
 *  - Filter by status / category / severity / sentiment / search / date
 *  - Expand a row to see the full message + AI suggestion + history
 *  - Transition status (Open → Triaged → Resolved / Won't fix)
 *  - Override AI category/severity (the AI is a starting point, not the truth)
 *  - Add admin notes (audit-trailed in `history[]`)
 */
import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  MessageSquareText, Star, Search, Filter, X, Bug, Lightbulb, Heart, AlertTriangle,
  HelpCircle, Sparkles, MessageCircle, ArrowRight, Clock, User as UserIcon, School,
  ChevronDown, ChevronUp, CheckCircle2, Archive, Smile, Meh, Frown
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  open:     { label: 'Open',       bg: 'bg-rose-100',    text: 'text-rose-700',    icon: AlertTriangle },
  triaged:  { label: 'Triaged',    bg: 'bg-blue-100',    text: 'text-blue-700',    icon: Clock },
  resolved: { label: 'Resolved',   bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
  wontfix:  { label: "Won't Fix",  bg: 'bg-slate-200',   text: 'text-slate-600',   icon: Archive },
};
const CATEGORY_STYLES = {
  improvement: { label: 'Improvement', bg: 'bg-indigo-50',  text: 'text-indigo-700', icon: Lightbulb },
  bug:         { label: 'Bug',         bg: 'bg-rose-50',    text: 'text-rose-700',   icon: Bug },
  complaint:   { label: 'Complaint',   bg: 'bg-amber-50',   text: 'text-amber-700',  icon: AlertTriangle },
  compliment:  { label: 'Compliment',  bg: 'bg-pink-50',    text: 'text-pink-700',   icon: Heart },
  confusion:   { label: 'Confusion',   bg: 'bg-yellow-50',  text: 'text-yellow-700', icon: HelpCircle },
  other:       { label: 'Other',       bg: 'bg-slate-50',   text: 'text-slate-600',  icon: MessageCircle },
};
const SEVERITY_STYLES = {
  low:    { label: 'Low',    bg: 'bg-emerald-50', text: 'text-emerald-700' },
  medium: { label: 'Medium', bg: 'bg-amber-50',   text: 'text-amber-700' },
  high:   { label: 'High',   bg: 'bg-rose-50',    text: 'text-rose-700' },
};
const SENTIMENT_ICON = {
  positive: { icon: Smile, color: 'text-emerald-500' },
  neutral:  { icon: Meh,   color: 'text-slate-400' },
  negative: { icon: Frown, color: 'text-rose-500' },
};

const ALL_STATUSES = ['open', 'triaged', 'resolved', 'wontfix'];
const ALL_CATEGORIES = ['improvement', 'bug', 'complaint', 'compliment', 'confusion', 'other'];
const ALL_SEVERITIES = ['low', 'medium', 'high'];
const ALL_SENTIMENTS = ['positive', 'neutral', 'negative'];

function fmtDT(s) {
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s || ''; }
}

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.open;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} text-[10px] font-heading uppercase tracking-wide`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function CategoryBadge({ category }) {
  const cfg = CATEGORY_STYLES[category] || CATEGORY_STYLES.other;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} text-[10px] font-heading`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function SeverityBadge({ severity }) {
  if (!severity) return null;
  const cfg = SEVERITY_STYLES[severity] || SEVERITY_STYLES.low;
  return <span className={`px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} text-[10px] font-heading uppercase`}>{cfg.label}</span>;
}

function FeedbackRow({ row, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(row.admin_notes || '');
  const [overrideCat, setOverrideCat] = useState(row.ai_category || '');
  const [overrideSev, setOverrideSev] = useState(row.ai_severity || '');
  const [submitting, setSubmitting] = useState(null);
  const SentIcon = (SENTIMENT_ICON[row.ai_sentiment] || SENTIMENT_ICON.neutral).icon;
  const sentColor = (SENTIMENT_ICON[row.ai_sentiment] || SENTIMENT_ICON.neutral).color;
  const history = Array.isArray(row.history) ? row.history : [];
  const isClosed = row.status === 'resolved' || row.status === 'wontfix';

  const transition = async (toStatus) => {
    setSubmitting(toStatus);
    try {
      const payload = { status: toStatus };
      if (notes.trim() && notes.trim() !== (row.admin_notes || '')) payload.admin_notes = notes.trim();
      if (overrideCat && overrideCat !== row.ai_category) payload.ai_category = overrideCat;
      if (overrideSev && overrideSev !== row.ai_severity) payload.ai_severity = overrideSev;
      await apiPut(`/admin/feedback/${row.feedback_id}`, payload);
      toast.success(`Marked as ${STATUS_STYLES[toStatus]?.label || toStatus}.`);
      onUpdate();
    } catch (err) {
      toast.error((err?.message || '').replace(/^HTTP \d+:\s*/, '') || `Couldn't mark ${toStatus}.`);
    } finally { setSubmitting(null); }
  };

  const saveOnly = async () => {
    const payload = {};
    if (notes.trim() !== (row.admin_notes || '')) payload.admin_notes = notes.trim();
    if (overrideCat && overrideCat !== row.ai_category) payload.ai_category = overrideCat;
    if (overrideSev && overrideSev !== row.ai_severity) payload.ai_severity = overrideSev;
    if (Object.keys(payload).length === 0) { toast.info("Nothing changed."); return; }
    setSubmitting('save');
    try {
      await apiPut(`/admin/feedback/${row.feedback_id}`, payload);
      toast.success('Saved.');
      onUpdate();
    } catch (err) {
      toast.error((err?.message || '').replace(/^HTTP \d+:\s*/, '') || 'Could not save.');
    } finally { setSubmitting(null); }
  };

  return (
    <Card data-testid={`feedback-row-${row.feedback_id}`}
      className={`p-4 transition-all ${expanded ? 'ring-2 ring-rose-100 shadow-md' : 'hover:border-rose-100'}`}>
      <button onClick={() => setExpanded(e => !e)}
        className="w-full text-left flex items-start gap-3"
        data-testid={`feedback-row-toggle-${row.feedback_id}`}>
        <div className="flex-shrink-0 mt-0.5">
          <SentIcon className={`w-5 h-5 ${sentColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <StatusBadge status={row.status} />
            <CategoryBadge category={row.ai_category || 'other'} />
            <SeverityBadge severity={row.ai_severity} />
            {row.rating > 0 && (
              <span className="inline-flex items-center gap-0.5 text-amber-500" title={`Rated ${row.rating}/5`}>
                {[1,2,3,4,5].map(n => (
                  <Star key={n} className={`w-3 h-3 ${n <= row.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                ))}
              </span>
            )}
            {!row.ai_category && (
              <span className="text-[10px] text-slate-400 font-body italic">analysing…</span>
            )}
            <span className="ml-auto text-[11px] text-slate-400 font-body">{fmtDT(row.created_at)}</span>
          </div>
          <p className="text-sm font-body text-slate-700 leading-snug line-clamp-2">{row.message}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px] text-slate-500 font-body">
            <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {row.user_email || row.user_id}</span>
            {row.user_school && <span className="inline-flex items-center gap-1"><School className="w-3 h-3" /> {row.user_school}</span>}
            {row.user_class && <span>Class {row.user_class}</span>}
            {row.subscription_tier && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] uppercase">{row.subscription_tier}</span>}
            {row.page_context && <span className="font-mono text-[10px] text-slate-400 truncate max-w-[200px]">{row.page_context}</span>}
          </div>
        </div>
        <div className="flex-shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4" data-testid={`feedback-expanded-${row.feedback_id}`}>
          <div>
            <p className="text-[11px] font-heading text-slate-500 uppercase tracking-wide mb-1">Full message</p>
            <blockquote className="text-sm font-body text-slate-700 leading-relaxed bg-slate-50 border-l-4 border-rose-200 px-4 py-3 rounded-r-lg whitespace-pre-wrap">
              {row.message}
            </blockquote>
          </div>

          {row.ai_suggestion && (
            <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl px-4 py-3 border border-indigo-100">
              <p className="text-[11px] font-heading text-indigo-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI suggestion
              </p>
              <p className="text-sm font-body text-slate-700 leading-snug">{row.ai_suggestion}</p>
              {row.ai_analysis_via && (
                <p className="text-[10px] text-indigo-400 font-body italic mt-1">via {row.ai_analysis_via}</p>
              )}
            </div>
          )}

          {!isClosed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] font-heading text-slate-500 uppercase tracking-wide mb-1">Override category</p>
                <select value={overrideCat || ''} onChange={(e) => setOverrideCat(e.target.value)}
                  data-testid={`feedback-override-category-${row.feedback_id}`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body focus:border-rose-300 focus:ring-1 focus:ring-rose-200">
                  <option value="">(keep AI's: {row.ai_category || 'pending'})</option>
                  {ALL_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_STYLES[c].label}</option>)}
                </select>
              </div>
              <div>
                <p className="text-[11px] font-heading text-slate-500 uppercase tracking-wide mb-1">Override severity</p>
                <select value={overrideSev || ''} onChange={(e) => setOverrideSev(e.target.value)}
                  data-testid={`feedback-override-severity-${row.feedback_id}`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body focus:border-rose-300 focus:ring-1 focus:ring-rose-200">
                  <option value="">(keep AI's: {row.ai_severity || 'pending'})</option>
                  {ALL_SEVERITIES.map(s => <option key={s} value={s}>{SEVERITY_STYLES[s].label}</option>)}
                </select>
              </div>
            </div>
          )}

          <div>
            <p className="text-[11px] font-heading text-slate-500 uppercase tracking-wide mb-1">Admin notes</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid={`feedback-admin-notes-${row.feedback_id}`}
              placeholder="What did you do? Who did you contact? Any follow-up?"
              rows={3}
              disabled={isClosed}
              className="resize-none font-body text-sm" />
          </div>

          {history.length > 0 && (
            <div>
              <p className="text-[11px] font-heading text-slate-500 uppercase tracking-wide mb-2">History</p>
              <ol className="space-y-1.5 text-xs font-body">
                {history.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 pl-2 border-l-2 border-slate-200">
                    <span className="text-slate-400 text-[10px] font-mono pt-0.5">{fmtDT(h.timestamp)}</span>
                    <span className="text-slate-600">
                      <span className="font-heading text-slate-700">{h.actor || 'admin'}</span>
                      {h.changes?.status && <> moved to <StatusBadge status={h.changes.status} /></>}
                      {h.changes?.admin_notes && <span className="text-slate-500 italic"> · "{h.changes.admin_notes.slice(0, 80)}{h.changes.admin_notes.length > 80 ? '…' : ''}"</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {!isClosed ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {row.status === 'open' && (
                <Button onClick={() => transition('triaged')} disabled={submitting != null}
                  data-testid={`feedback-action-triaged-${row.feedback_id}`}
                  className="font-heading text-xs bg-blue-500 hover:bg-blue-600 text-white">
                  <Clock className="w-3.5 h-3.5 mr-1.5" /> Mark Triaged
                </Button>
              )}
              <Button onClick={() => transition('resolved')} disabled={submitting != null}
                data-testid={`feedback-action-resolved-${row.feedback_id}`}
                className="font-heading text-xs bg-emerald-500 hover:bg-emerald-600 text-white">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark Resolved
              </Button>
              <Button onClick={() => transition('wontfix')} disabled={submitting != null} variant="outline"
                data-testid={`feedback-action-wontfix-${row.feedback_id}`}
                className="font-heading text-xs">
                <Archive className="w-3.5 h-3.5 mr-1.5" /> Won't Fix
              </Button>
              <Button onClick={saveOnly} disabled={submitting != null} variant="ghost"
                data-testid={`feedback-action-save-${row.feedback_id}`}
                className="font-heading text-xs ml-auto">
                Save changes
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-body text-emerald-700">
                This ticket is {STATUS_STYLES[row.status].label.toLowerCase()}. Full audit trail preserved above.
              </span>
              <Button onClick={() => transition('open')} disabled={submitting != null} variant="ghost"
                data-testid={`feedback-action-reopen-${row.feedback_id}`}
                className="ml-auto font-heading text-xs">
                Reopen
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function FeedbackDashboard() {
  const [data, setData] = useState({ feedback: [], summary: null, filtered_count: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // filters
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (category) params.set('category', category);
      if (severity) params.set('severity', severity);
      if (sentiment) params.set('sentiment', sentiment);
      if (q.trim()) params.set('q', q.trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const path = `/admin/feedback${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await apiGet(path);
      setData({ feedback: res.feedback || [], summary: res.summary || null, filtered_count: res.filtered_count || 0 });
    } catch (e) {
      setErr(e?.message || 'Failed to load feedback.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category, severity, sentiment, dateFrom, dateTo]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { load(); /* eslint-disable-next-line */ }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const summary = data.summary;
  const hasFilters = useMemo(() => Boolean(status || category || severity || sentiment || q.trim() || dateFrom || dateTo), [status, category, severity, sentiment, q, dateFrom, dateTo]);

  const clearFilters = () => { setStatus(''); setCategory(''); setSeverity(''); setSentiment(''); setQ(''); setDateFrom(''); setDateTo(''); };

  return (
    <div className="space-y-6" data-testid="feedback-dashboard">
      {/* Hero */}
      <Card className="p-6 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 border-rose-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shadow-md flex-shrink-0">
            <MessageSquareText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold text-slate-800">User Feedback</h2>
            <p className="text-sm font-body text-slate-600 mt-1 max-w-2xl">
              Every piece of feedback users send through the Study Buddy is here. The AI tags each one with category, severity & a quick suggestion — but the call is yours. Triage, respond, decide.
            </p>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <StatCard label="Total" value={summary.total} accent="slate" testid="stat-total" />
            <StatCard label="Open" value={summary.open} accent="rose" testid="stat-open" />
            <StatCard label="Triaged" value={summary.triaged} accent="blue" testid="stat-triaged" />
            <StatCard label="Resolved" value={summary.resolved} accent="emerald" testid="stat-resolved" />
          </div>
        )}

        {summary && (summary.by_category || summary.by_severity) && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-rose-100">
              <p className="text-[10px] font-heading text-slate-500 uppercase tracking-wide mb-2">By category</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_CATEGORIES.map(c => (
                  <button key={c} onClick={() => setCategory(category === c ? '' : c)}
                    data-testid={`stat-category-${c}`}
                    className={`text-[10px] font-heading px-2 py-1 rounded-full border transition-all ${category === c ? `${CATEGORY_STYLES[c].bg} ${CATEGORY_STYLES[c].text} border-current` : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    {CATEGORY_STYLES[c].label}: <span className="font-mono">{summary.by_category?.[c] ?? 0}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white/70 backdrop-blur rounded-xl p-3 border border-rose-100">
              <p className="text-[10px] font-heading text-slate-500 uppercase tracking-wide mb-2">By severity</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SEVERITIES.map(s => (
                  <button key={s} onClick={() => setSeverity(severity === s ? '' : s)}
                    data-testid={`stat-severity-${s}`}
                    className={`text-[10px] font-heading px-2 py-1 rounded-full border transition-all ${severity === s ? `${SEVERITY_STYLES[s].bg} ${SEVERITY_STYLES[s].text} border-current` : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    {SEVERITY_STYLES[s].label}: <span className="font-mono">{summary.by_severity?.[s] ?? 0}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search messages, user email, AI suggestion…"
              data-testid="feedback-search"
              className="pl-9" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="feedback-filter-status"
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
            <option value="">All statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
          </select>
          <Button variant="ghost" size="sm" onClick={() => setShowAdvanced(a => !a)}
            data-testid="feedback-filter-advanced-toggle"
            className="font-heading text-xs">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> {showAdvanced ? 'Hide' : 'Advanced'}
          </Button>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}
              data-testid="feedback-filter-clear"
              className="font-heading text-xs text-rose-600 hover:text-rose-700">
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear
            </Button>
          )}
        </div>
        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100">
            <div>
              <p className="text-[10px] font-heading text-slate-500 uppercase mb-1">Category</p>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="feedback-filter-category"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
                <option value="">Any</option>
                {ALL_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_STYLES[c].label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-heading text-slate-500 uppercase mb-1">Severity</p>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                data-testid="feedback-filter-severity"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
                <option value="">Any</option>
                {ALL_SEVERITIES.map(s => <option key={s} value={s}>{SEVERITY_STYLES[s].label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-heading text-slate-500 uppercase mb-1">Sentiment</p>
              <select value={sentiment} onChange={(e) => setSentiment(e.target.value)}
                data-testid="feedback-filter-sentiment"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
                <option value="">Any</option>
                {ALL_SENTIMENTS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] font-heading text-slate-500 uppercase mb-1">From</p>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="feedback-filter-date-from" />
              </div>
              <div>
                <p className="text-[10px] font-heading text-slate-500 uppercase mb-1">To</p>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  data-testid="feedback-filter-date-to" />
              </div>
            </div>
          </div>
        )}
        {summary && (
          <p className="mt-3 text-[11px] font-body text-slate-500" data-testid="feedback-counter">
            Showing <span className="font-heading text-slate-700">{data.filtered_count}</span> of <span className="font-heading text-slate-700">{summary.total}</span> all-time.
          </p>
        )}
      </Card>

      {/* List */}
      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-500 font-body" data-testid="feedback-loading">
          Loading feedback…
        </Card>
      ) : err ? (
        <Card className="p-8 text-center text-sm text-rose-600 font-body border-rose-200" data-testid="feedback-error">
          {err}
        </Card>
      ) : data.feedback.length === 0 ? (
        <Card className="p-12 text-center" data-testid="feedback-empty">
          <MessageSquareText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-heading text-slate-600">No feedback {hasFilters ? 'matches these filters' : 'yet'}.</p>
          <p className="text-xs font-body text-slate-400 mt-1">
            {hasFilters ? 'Try clearing some filters.' : 'When students submit feedback through the Study Buddy, it shows up here.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.feedback.map(row => (
            <FeedbackRow key={row.feedback_id} row={row} onUpdate={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const map = {
    slate:    'from-slate-100 to-white text-slate-700',
    rose:     'from-rose-100 to-white text-rose-700',
    blue:     'from-blue-100 to-white text-blue-700',
    emerald:  'from-emerald-100 to-white text-emerald-700',
  };
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${map[accent] || map.slate} rounded-xl px-4 py-3 border border-white shadow-sm`}>
      <p className="text-[10px] font-heading uppercase tracking-wide opacity-70">{label}</p>
      <p className="font-display text-2xl font-bold mt-0.5">{value ?? 0}</p>
    </div>
  );
}
